import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { searchAnimalLocal, addPendingOp, addAnimalToCache } from '../../services/offlineDB';
import axios from 'axios';

const RegistrarParicionForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [madre, setMadre] = useState(null);
  const [buscarQuery, setBuscarQuery] = useState('');
  const [buscandoMadre, setBuscandoMadre] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [epCalculado, setEpCalculado] = useState(null);

  const [formData, setFormData] = useState({
    madre_id: '',
    fecha_paricion: new Date().toISOString().split('T')[0],
    tipo: 'normal',
    dificultad: 'sin_ayuda',
    sexo_ternero: 'macho',
    peso_nacimiento: '',
    ternero_VID: '',
    ternero_IDE: '',
    ternero_raza: '',
    ternero_color: '',
    fecha_destete: '',
    peso_destete: '',
    peso_madre_destete: '',
    observaciones: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if ((name === 'peso_destete' || name === 'peso_madre_destete')) {
      const ternero = name === 'peso_destete' ? parseFloat(value) : parseFloat(formData.peso_destete);
      const madreP = name === 'peso_madre_destete' ? parseFloat(value) : parseFloat(formData.peso_madre_destete);
      if (ternero > 0 && madreP > 0) setEpCalculado(((ternero / madreP) * 100).toFixed(1));
    }
  };

  // ── Search animal: online → API, offline → IndexedDB cache ──
  const buscarMadre = async () => {
    if (!buscarQuery.trim()) return;
    setBuscandoMadre(true);
    setError('');
    setResultados([]);

    try {
      if (navigator.onLine) {
        // Try API first
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${BACKEND_URL}/api/animales/buscar/rfid/${encodeURIComponent(buscarQuery.trim())}`, {
          params: { establecimiento_id: user.establecimiento_id },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const animal = res.data;
        if (animal.sexo !== 'hembra') { setError('Este animal no es hembra.'); return; }
        seleccionarMadre(animal);
      } else {
        throw new Error('offline');
      }
    } catch {
      // Offline or API failed → search local cache
      try {
        const localResults = await searchAnimalLocal(buscarQuery.trim(), user.establecimiento_id);
        const hembras = localResults.filter(a => a.sexo === 'hembra');
        if (hembras.length === 1) {
          seleccionarMadre(hembras[0]);
        } else if (hembras.length > 1) {
          setResultados(hembras.slice(0, 10));
        } else {
          setError('Animal no encontrado en cache local. Conectate a internet para sincronizar datos.');
        }
      } catch {
        setError('Error buscando en cache local.');
      }
    } finally { setBuscandoMadre(false); }
  };

  const seleccionarMadre = (animal) => {
    setMadre(animal);
    setFormData(prev => ({ ...prev, madre_id: animal.id, ternero_raza: animal.raza || '' }));
    setResultados([]);
  };

  // ── Submit: online → API, offline → IndexedDB queue ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.ternero_VID && !formData.ternero_IDE) {
      setError('Ingresa al menos el VID o IDE del ternero para registrarlo.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        establecimiento_id: user.establecimiento_id,
        peso_nacimiento: formData.peso_nacimiento ? parseFloat(formData.peso_nacimiento) : null,
        peso_destete: formData.peso_destete ? parseFloat(formData.peso_destete) : null,
        peso_madre_destete: formData.peso_madre_destete ? parseFloat(formData.peso_madre_destete) : null,
      };

      if (payload.fecha_destete && payload.fecha_paricion) {
        payload.edad_destete_dias = Math.floor((new Date(payload.fecha_destete) - new Date(payload.fecha_paricion)) / 86400000);
      }
      if (payload.peso_destete && payload.peso_madre_destete) {
        payload.eficiencia_productiva = parseFloat(epCalculado);
      }

      Object.keys(payload).forEach(key => { if (payload[key] === '' || payload[key] === null) delete payload[key]; });

      if (navigator.onLine) {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.post(`${BACKEND_URL}/api/pariciones/`, payload, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = res.data;
        let msg = 'Paricion registrada';
        if (data.ternero_creado) msg += ` — Ternero ${data.ternero_VID || data.ternero_IDE} creado`;
        if (data.padre_encontrado) msg += ' (padre vinculado)';
        if (epCalculado) msg += ` — EP: ${epCalculado}%`;
        setSuccessMessage(msg);
      } else {
        // ── OFFLINE: save to IndexedDB ──
        await addPendingOp({ method: 'POST', url: '/pariciones/', data: payload });

        // Also add the ternero to local cache so it shows up in searches
        if (formData.ternero_VID || formData.ternero_IDE) {
          const localTernero = {
            id: `local_${Date.now()}`,
            establecimiento_id: user.establecimiento_id,
            VID: formData.ternero_VID || '',
            IDE: formData.ternero_IDE || '',
            sexo: formData.sexo_ternero,
            raza: formData.ternero_raza || madre?.raza || '',
            color: formData.ternero_color || madre?.color || '',
            categoria: formData.sexo_ternero === 'macho' ? 'ternero' : 'ternero',
            estado: 'activo',
            fecha_nacimiento: formData.fecha_paricion,
            madre_id: formData.madre_id,
            origen: 'nacimiento',
            _offline: true,
          };
          await addAnimalToCache(localTernero);
        }

        setSuccessMessage('GUARDADO LOCALMENTE — Se sincronizara cuando vuelvas a tener internet.');
      }

      // Reset form
      setFormData({
        madre_id: '', fecha_paricion: new Date().toISOString().split('T')[0],
        tipo: 'normal', dificultad: 'sin_ayuda', sexo_ternero: 'macho',
        peso_nacimiento: '', ternero_VID: '', ternero_IDE: '', ternero_raza: '', ternero_color: '',
        fecha_destete: '', peso_destete: '', peso_madre_destete: '', observaciones: '',
      });
      setMadre(null);
      setEpCalculado(null);
      if (onSuccess) setTimeout(() => onSuccess(), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar paricion');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6" data-testid="paricion-form">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Registrar Paricion</h2>
        <div className="flex items-center space-x-2">
          {!navigator.onLine && <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">MODO CAMPO</span>}
          {onCancel && <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 text-lg">&times;</button>}
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="paricion-error">{error}</div>}
      {successMessage && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${successMessage.includes('LOCALMENTE') ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-green-50 border border-green-200 text-green-700'}`} data-testid="paricion-success">
          {successMessage}
        </div>
      )}

      {/* Search madre */}
      {!madre && (
        <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
          <h3 className="text-sm font-semibold text-purple-800 mb-3">Buscar Madre por VID / IDE / RFID</h3>
          <div className="flex space-x-2">
            <input type="text" value={buscarQuery} onChange={(e) => setBuscarQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarMadre()}
              placeholder="Escanea RFID o ingresa VID/IDE..."
              className="flex-1 px-3 py-2 border border-purple-300 rounded-lg font-mono focus:ring-2 focus:ring-purple-500"
              autoFocus data-testid="buscar-madre-input" />
            <button onClick={buscarMadre} disabled={buscandoMadre}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              data-testid="buscar-madre-btn">{buscandoMadre ? '...' : 'Buscar'}</button>
          </div>
          {!navigator.onLine && <p className="text-xs text-amber-600 mt-2">Buscando en datos locales (modo campo)</p>}

          {/* Multiple results from local search */}
          {resultados.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-xs text-purple-600 mb-1">{resultados.length} resultados — selecciona la madre:</p>
              {resultados.map(a => (
                <button key={a.id} onClick={() => seleccionarMadre(a)}
                  className="w-full text-left p-2 bg-white border rounded-lg hover:bg-purple-100 text-sm flex justify-between">
                  <span><strong>VID:</strong> {a.VID} | <strong>IDE:</strong> {a.IDE || '-'}</span>
                  <span className="text-gray-500">{a.raza} | {a.categoria?.replace('_',' ')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Madre selected */}
      {madre && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-semibold text-green-800 mb-2">Madre Seleccionada</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">VID:</span> {madre.VID}</div>
                <div><span className="font-medium">IDE:</span> {madre.IDE || '-'}</div>
                <div><span className="font-medium">Categoria:</span> {madre.categoria?.replace('_',' ')}</div>
                <div><span className="font-medium">Raza:</span> {madre.raza}</div>
              </div>
            </div>
            <button onClick={() => { setMadre(null); setFormData(prev => ({ ...prev, madre_id: '' })); }}
              className="text-red-600 hover:text-red-800 text-sm font-medium">Cambiar</button>
          </div>
        </div>
      )}

      {/* Form */}
      {madre && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* DATOS DEL PARTO */}
          <div className="border-b pb-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3">Datos del Parto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Paricion *</label>
                <input type="date" name="fecha_paricion" value={formData.fecha_paricion} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" required data-testid="fecha-paricion" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select name="tipo" value={formData.tipo} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500">
                  <option value="normal">Normal</option><option value="gemelar">Gemelar</option><option value="cesarea">Cesarea</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dificultad</label>
                <select name="dificultad" value={formData.dificultad} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500">
                  <option value="sin_ayuda">Sin Ayuda</option><option value="ayuda_leve">Ayuda Leve</option><option value="ayuda_considerable">Ayuda Considerable</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sexo Ternero *</label>
                <select name="sexo_ternero" value={formData.sexo_ternero} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" required>
                  <option value="macho">Macho</option><option value="hembra">Hembra</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Peso Nacimiento (kg)</label>
                <input type="number" step="0.1" name="peso_nacimiento" value={formData.peso_nacimiento} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" placeholder="30-40" />
              </div>
            </div>
          </div>

          {/* DATOS DEL TERNERO */}
          <div className="border-b pb-4">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Datos del Ternero</h3>
            <p className="text-xs text-gray-500 mb-3">Se crea automaticamente como animal nuevo vinculado a la madre</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VID del Ternero *</label>
                <input type="text" name="ternero_VID" value={formData.ternero_VID} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg font-mono focus:ring-2 focus:ring-blue-500" placeholder="Ej: 1050" data-testid="ternero-vid" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IDE del Ternero (RFID)</label>
                <input type="text" name="ternero_IDE" value={formData.ternero_IDE} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg font-mono focus:ring-2 focus:ring-blue-500" placeholder="Ej: 982712345678" data-testid="ternero-ide" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raza</label>
                <input type="text" name="ternero_raza" value={formData.ternero_raza} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={madre?.raza || 'Hereda de madre'} data-testid="ternero-raza" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input type="text" name="ternero_color" value={formData.ternero_color} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={madre?.color || 'Hereda de madre'} data-testid="ternero-color" />
              </div>
            </div>
          </div>

          {/* DESTETE */}
          <div className="border-b pb-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3">Destete (Opcional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Destete</label>
                <input type="date" name="fecha_destete" value={formData.fecha_destete} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Peso Ternero Destete (kg)</label>
                <input type="number" step="0.1" name="peso_destete" value={formData.peso_destete} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" placeholder="180-240" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Peso Madre Destete (kg)</label>
                <input type="number" step="0.1" name="peso_madre_destete" value={formData.peso_madre_destete} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" placeholder="380-450" />
              </div>
              {epCalculado && (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800">EP Calculado</p>
                  <p className="text-3xl font-bold text-green-600">{epCalculado}%</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} rows="2"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" />
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            {onCancel && <button type="button" onClick={onCancel} className="px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>}
            <button type="submit" disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              data-testid="submit-paricion">{loading ? 'Guardando...' : navigator.onLine ? 'Registrar' : 'Guardar Local'}</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrarParicionForm;
